/**
 * @file
 * @fileOverview Holds the list of 3D objects and the properties per object along with assets you can set for images and 3D models.
 * @version 1.0.0
 * @author Parker Eli Bidigare
 * @date 2024-05-26
 */

/**
 * manage 3D objects and assets for scene
 * @class
 * @classdesc manage 3D objects and assets for scene
 */
class objectRegistry {
  /**
   * Create an object register
   * @constructor
   */
  constructor() {
    /**
     * The main list of 3D object elements.
     * @type {HTMLElement[]}
     */
    this.objects = [];

    /**
     * The main list of asset item/img elements.
     * @type {HTMLElement[]}
     */
    this.assets = [];
  }

  /**
   * Update an attribute to an element associated to a 3D object element.
   * @param {string} id - 3D object ID.
   * @param {string} attributeType - the name of the attribute to the element.
   * @param {*} value - the value of the attribute to the element.
   * @example
   * const objReg = new objectRegistery();
   * objReg.updateAttrById('cube-1', 'radius', 2);
   */
  updateAttrById(id, attributeType, value) {
    this.objects.forEach((item) => {
      if (item.id === id) {
        item[attributeType] = value;
      }
    });
  }

  /**
   * Update an attribute string with multiple attributes in it like Material attribute (for an element).
   * @param {string} id - 3D object ID.
   * @param {string} attributeType - the name of the attribute to the element.
   * @param {string} value - the value of the attribute to the element.
   * @example
   * const objReg = new objectRegistery();
   * objReg.editAttr('color=red; metalness=0.2', 'metalness', '0.8');
   */
  editAttr(attrString, type, value) {
    // Convert the attribute string to an array of key-value pairs
    let attrs = attrString
      .split(';')
      .map((attr) => attr.trim())
      .filter((attr) => attr);

    // Create a dictionary from the key-value pairs
    let attrDict = {};
    attrs.forEach((attr) => {
      let [key, val] = attr.split(':').map((item) => item.trim());
      attrDict[key] = val;
    });
    attrDict[type] = value;
    let newAttrString = Object.entries(attrDict)
      .map(([key, val]) => `${key}: ${val}`)
      .join('; ');
    newAttrString += ';';

    return newAttrString;
  }

  /**
   * Update an attribute to an element within the material attribute for a 3D object element.
   * @param {string} id - 3D object ID
   * @param {string} attributeType - the name of the attribute to the element.
   * @param {*} value - the value of the attribute to the element.
   * @example
   * const objReg = new objectRegistery();
   * objReg.updateMaterialAttribute('cube-1', 'metalness', 0.2);
   */
  updateMaterialAttribute(id, type, value) {
    this.objects.forEach((item) => {
      if (item.id === id) {
        let mat = item['material'];
        mat = this.editAttr(mat, type, value);
        item['material'] = mat;
      }
    });
  }

  /**
   * Add an image asset element
   * @param {string} id - asset ID
   * @param {string} src - the directory of the image source.
   * @example
   * const objReg = new objectRegistery();
   * objReg.addImageAsset('texture', 'path/to/image.png);
   */
  addImageAsset(id, src) {
    let asset = document.createElement('img');
    asset.setAttribute('src', src);
    asset.id = id;
    asset.setAttribute('name', 'image');
    let assetTag = document.getElementById('assets');
    assetTag.appendChild(assetTag);
  }

  /**
   * Create a sphere 3D object
   * @param {string} id - Spheres ID
   * @param {string} className - set a css class to the sphere element.
   * @param {string} color - set a color to the sphere element
   * @param {string} position - set x,y,z position of sphere ("x y z")
   * @param {number} radius - set the radius size of the sphere
   * @param {object} attributes - additional attributes to apply to sphere element
   * @example
   * const objReg = new objectRegistery();
   * objReg.createSphere('1','sphereClass','red',0.5,{});
   */
  createSphere(id, className, position, color, radius) {
    let sphere = document.createElement('a-sphere');
    sphere.id = id;
    sphere.className = className;
    sphere.name = 'sphere';
    sphere.setAttribute('position', position);
    sphere.setAttribute('radius', radius);
    sphere.setAttribute('color', color);
    this.objects.push(sphere);
  }

  /**
   * Create a box 3D object
   * @param {string} id - boxes ID
   * @param {string} className - set a css class to the box element.
   * @param {string} color - set a color to the box element
   * @param {string} position - set x,y,z position of box ("x y z")
   * @param {number} width - set width size of the box
   * @param {number} height - set height size of the box
   * @param {number} depth - set depth size of the box.
   * @param {object} attributes - additional attributes to apply to box element
   * @example
   * const objReg = new objectRegistery();
   * objReg.createBox('1','boxClass','red',0.5,0.5,0.5,{});
   */
  createBox(id, className, position, color, width, height, depth) {
    let box = document.createElement('a-box');
    box.id = id;
    box.className = className;
    box.name = 'box';
    box.setAttribute('position', position);
    box.setAttribute('width', width);
    box.setAttribute('height', height);
    box.setAttribute('depth', depth);
    box.setAttribute('color', color);
    this.objects.push(box);
  }

  /**
   * Create a cylinder 3D object
   * @param {string} id - Cylinder ID
   * @param {string} className - set a css class to the cylinder element.
   * @param {string} color - set a color to the cylinder element
   * @param {string} position - set x,y,z position of cylinder ("x y z")
   * @param {number} radius - set radius size of the cylinder
   * @param {number} height - set height size of the cylinder
   * @param {object} attributes - additional attributes to apply to cylinder element
   * @example
   * const objReg = new objectRegistery();
   * objReg.createCylinder('1','cylinderClass','red',0.5,0.5,{});
   */
  createCylinder(id, className, position, color, radius, height) {
    let cylinder = document.createElement('a-cylinder');
    cylinder.id = id;
    cylinder.className = className;
    cylinder.name = 'cylinder';
    cylinder.setAttribute('position', position);
    cylinder.setAttribute('radius', radius);
    cylinder.setAttribute('height', height);
    cylinder.setAttribute('color', color);
    this.objects.push(cylinder);
  }

  /**
   * Create a plane 3D object
   * @param {string} id - Plane ID
   * @param {string} className - set a css class to the Plane element.
   * @param {string} color - set a color to the Plane element
   * @param {string} position - set x,y,z position of plane ("x y z")
   * @param {number} width - set width size of the plane
   * @param {number} height - set height size of the plane
   * @param {object} attributes - additional attributes to apply to plane element
   * @example
   * const objReg = new objectRegistery();
   * objReg.createPlane('1','planeClass','red',0.5,0.5,{});
   */
  createPlane(id, className, position, color, width, height) {
    let plane = document.createElement('a-plane');
    plane.id = id;
    plane.className = className;
    plane.name = 'plane';
    plane.setAttribute('position', position);
    plane.setAttribute('width', width);
    plane.setAttribute('height', height);
    plane.setAttribute('color', color);
    this.objects.push(plane);
  }

  /**
   * Add an asset which can be 3D models
   * @param {string} id - asset ID
   * @param {string} src - the directory of the asset source.
   * @example
   * const objReg = new objectRegistery();
   * objReg.addAsset('asset-1', 'path/to/object.glb);
   */
  addAsset(id, src) {
    let asset = document.createElement('a-assest-item');
    asset.setAttribute('src', src);
    asset.id = id;
    asset.name = 'asset';
    let assetTag = document.getElementById('assets');
    assetTag.appendChild(assetTag);
  }

  /**
   * Create a gltf entity element
   * @param {string} id - object ID
   * @param {string} src - the source of the 3D object with id to asset
   * @param {string} position - set x,y,z of the object.
   * @example
   * const objReg = new objectRegistery();
   * objReg.CreateGltfObject('gltf-1', '#gltf-obj','1 2 3');
   */
  CreateGltfObject(id, src, position) {
    let gltf = document.createElement('a-gltf-model');
    gltf.id = id;
    gltf.name = 'gltf';
    gltf.className = className;
    gltf.setAttribute('position', position);
    gltf.setAttribute('src', src);
    this.objects.push(gltf);
  }

  /**
   * enables or disabled if the object casts a shadow
   * @param {string} id - object id
   * @param {string} shadow - toggle shadow
   * @example
   * const objReg = new objectRegistery();
   * objReg.enableShadowForObject('gltf-1','true')
   */
  enableShadowForObject(id, shadow) {
    this.updateAttrById(id, 'shadow', shadow);
  }

  setObjectPosition(id, x, y, z) {
    this.updateAttrById(id, 'position', x + ' ' + y + ' ' + z);
  }

  getObjectsRegistry() {
    return this.objects;
  }

  getAssetResigtry() {
    return this.assets;
  }

  setMaterialMetalness(id, metal) {
    this.updateMaterialAttribute(id, 'metalness', metal);
  }

  setMaterialNormalMap(id, normalMapImageId) {
    this.updateMaterialAttribute(id, 'normalMap', normalMapImageId);
  }

  setMaterialEnvMap(id, envMapImageId) {
    this.updateMaterialAttribute(id, 'envMap', envMapImageId);
  }

  setMaterialroghness(id, roughness) {
    this.updateMaterialAttribute(id, 'roughness', roughness);
  }

  /**
   *
   * @param {number} id - id of the object
   * @param {boolean} fog - set fog true or false
   */
  setMaterialFog(id, fog) {
    this.updateMaterialAttribute(id, 'fog', fog);
  }

  /**
   *
   * @param {number} id - object id
   * @param {boolean} wire - set object wireframe true or not
   */
  setMaterialWireframe(id, wire) {
    this.updateMaterialAttribute(id, wire);
  }

  getObjectExists(id) {
    this.objects.forEach((obj) => {
      if (obj.id === id) {
        return obj;
      }
    });
    return null;
  }

  draw() {
    this.objects.forEach((item) => {
      let tagCheck = document.getElementById(item.id);
      if (tagCheck == null) {
        let scene = document.querySelector('a-scene');
        scene.appendChild(item);
      }
    });
  }
}

// Export the function for use in the entry point
export default objectRegistry;
